import React from "react"
import DataTable from "../../../../components/atoms/table/DataTable.atom"
import Column from "../../../../components/atoms/table/Column.atom"
import { formatDateWithShortMonth } from "../../../../utils/date.utils"
import {
  VACATION_DAY_TYPES,
  VACATION_DAYS_VARIANTS,
  VACATION_HISTORY_APPROVED_STATUS,
  VACATION_STATES,
  VACATION_STATUS_CHIP_VARIANTS,
} from "../../../../consts/modules/vacations.consts"
import VacationDayVariantChip from "../../../../components/molecules/VacationDayVariantChip.molecule"
import { clsx } from "../../../../utils/strings.utils"
import VacationStatusChip from "../../../../components/molecules/VacationStatusChip.molecule"

/** @typedef Props {Object}
 * @property {VacationHistory} data
 * @property {boolean} isUsingForeignSchema
 */

/**
 * @typedef {{
 *  status: (typeof VACATION_STATES)[keyof typeof VACATION_STATES]
 *  pendingDayPlusPay: boolean
 *  modifierName: string
 * }} StatusData
 */

export default function VacationHistoryRowExpansion(
  /** @type {Props} */ { data, isUsingForeignSchema }
) {
  /**
   * @param {StatusData} statusData
   */
  function vacationStatusHasTooltipData(statusData) {
    return [
      VACATION_STATES.CANCELED,
      VACATION_STATES.PENDING,
      VACATION_STATES.APPROVED_AND_PENDING_PLUS_DAYS_PAY_DATE,
      VACATION_STATES.CHANGE_REQUEST,
      VACATION_STATES.CANCEL_REQUEST,
    ].includes(statusData.status)
  }

  /**
   * @param {StatusData} statusData
   */
  function getVacationStatusTooltipData(statusData) {
    const PENDING = {
      [VACATION_STATES.PENDING]: "Pendiente por aprobar",
      [VACATION_STATES.CHANGE_REQUEST]: "Pendiente de cambio",
      [VACATION_STATES.CANCEL_REQUEST]: "Pendiente de cancelación",
      [VACATION_STATES.APPROVED_AND_PENDING_PLUS_DAYS_PAY_DATE]:
        "Pendiente de pago de días +",
    }

    const CANCELED = {
      [VACATION_STATES.CANCELED]: "Cancelado",
    }

    if (PENDING[statusData.status]) {
      return PENDING[statusData.status]
    }

    if (CANCELED[statusData.status]) {
      return `${CANCELED[statusData.status]} ${statusData.modifierName ? `por ${statusData.modifierName}` : ""}`
    }

    return ""
  }

  /**
   * @param {StatusData} statusData
   */
  function getVacationStatusLabel(statusData) {
    const DEFAULT_PENDING_LABEL = VACATION_HISTORY_APPROVED_STATUS.PENDING

    const STATUS_LABELS = {
      [VACATION_STATES.PENDING]: DEFAULT_PENDING_LABEL,
      [VACATION_STATES.CHANGE_REQUEST]: DEFAULT_PENDING_LABEL,
      [VACATION_STATES.CANCEL_REQUEST]: DEFAULT_PENDING_LABEL,
      [VACATION_STATES.APPROVED_AND_PENDING_PLUS_DAYS_PAY_DATE]:
        VACATION_HISTORY_APPROVED_STATUS.APPROVED,
      [VACATION_STATES.APPROVED]: VACATION_HISTORY_APPROVED_STATUS.APPROVED,
      [VACATION_STATES.REJECTED]: VACATION_HISTORY_APPROVED_STATUS.REJECTED,
      [VACATION_STATES.CANCELED]: VACATION_HISTORY_APPROVED_STATUS.CANCELED,
    }

    return STATUS_LABELS[statusData.status]
  }

  /**
   * @param {StatusData} statusData
   */
  function getVacationStatusClassName(statusData) {
    if (
      statusData.status ===
      VACATION_STATES.APPROVED_AND_PENDING_PLUS_DAYS_PAY_DATE
    ) {
      return VACATION_STATES.APPROVED
    }

    return statusData.status
  }

  return (
    <DataTable
      className="vacations-subtable"
      value={data.vacations}
      responsiveLayout="scroll"
      scrollable
      scrollHeight="100%"
    >
      <Column
        field="requestedDate"
        className="requested-date-column"
        header="Fecha de solicitud"
        body={(data) => (
          <p className="period">
            {formatDateWithShortMonth(data.requestedDate)}
          </p>
        )}
      />
      <Column
        field="takenDateStart"
        className="taken-date-column"
        header="Fechas solicitadas"
        body={(data) => (
          <p className="period">
            {formatDateWithShortMonth(data.takenDateStart)} -{" "}
            {formatDateWithShortMonth(data.takenDateEnd)}
          </p>
        )}
      />
      <Column
        field="motive"
        className="motive-column"
        header="Motivo"
        body={(data) => {
          return (
            <p className="motive" title={data.motive}>
              {data.motive}
            </p>
          )
        }}
      />

      <Column
        className="type-column"
        header="Tipo"
        field="daysTaken"
        body={(data) => {
          const isCanceledOrRejected = [
            VACATION_STATES.CANCELED,
            VACATION_STATES.REJECTED,
          ].includes(data.statusData.status)

          return (
            <div
              className={`taken-days-kind-row-wrapper flex gap-4 align-items-center ${isCanceledOrRejected ? "opacity-50" : ""}`}
            >
              {!isUsingForeignSchema && data.vacationDays.bucket > 0 && (
                <div className="chip-wrapper flex gap-2 align-items-center h-full">
                  <VacationDayVariantChip
                    variant={VACATION_DAYS_VARIANTS.LONG}
                    dayType={VACATION_DAY_TYPES.BUCKET}
                  />
                  <p className="amount">{data.vacationDays.bucket}</p>
                </div>
              )}
              {data.vacationDays.plus > 0 && (
                <div className="chip-wrapper flex gap-2 align-items-center h-full">
                  <VacationDayVariantChip
                    variant={VACATION_DAYS_VARIANTS.LONG}
                    dayType={VACATION_DAY_TYPES.PLUS}
                  />
                  <p className="amount">{data.vacationDays.plus}</p>
                </div>
              )}
              {data.vacationDays.accorded > 0 && (
                <div className="chip-wrapper flex gap-2 align-items-center h-full">
                  <VacationDayVariantChip
                    variant={VACATION_DAYS_VARIANTS.LONG}
                    dayType={VACATION_DAY_TYPES.ACCORDED}
                  />
                  <p className="amount">{data.vacationDays.accorded}</p>
                </div>
              )}
              {data.vacationDays.additional > 0 && (
                <div className="chip-wrapper flex gap-2 align-items-center h-full">
                  <VacationDayVariantChip
                    variant={VACATION_DAYS_VARIANTS.LONG}
                    dayType={VACATION_DAY_TYPES.EXTRA}
                  />
                  <p className="amount">{data.vacationDays.additional}</p>
                </div>
              )}
            </div>
          )
        }}
      />
      <Column
        className="pay-date-column"
        field="payDate"
        header="Fecha de pago"
        body={(data) => {
          if (isNaN(Date.parse(data.payDate))) {
            return (
              <p
                className={clsx(
                  "period",
                  data.payDate === "N/A" && "opacity-50"
                )}
              >
                {data.payDate}
              </p>
            )
          } else {
            return (
              <p className="period">{formatDateWithShortMonth(data.payDate)}</p>
            )
          }
        }}
      />
      <Column
        className="status-column"
        field="status"
        header="Estado"
        body={(data) => (
          <div className="flex gap-2 align-items-center">
            <VacationStatusChip
              status={getVacationStatusClassName(data.statusData)}
              pendingDayPlusPay={data.statusData.pendingDayPlusPay}
              showTooltip={vacationStatusHasTooltipData(data.statusData)}
              tooltipContent={{
                title: getVacationStatusTooltipData(data.statusData),
              }}
              tooltipConfig={{
                position: "top",
              }}
            />
            {getVacationStatusLabel(data.statusData)}
          </div>
        )}
      />
    </DataTable>
  )
}
